import { Collapse } from "@blueprintjs/core";
import { Caret } from "modules/common/components/collapse/Caret";
import { Peoples } from "modules/orders-manage/details/Peoples";
import React, { useCallback } from "react";
import { PlanGrid } from "../models/plan-scale";
import { CellSelection, PlanColumn, PlanProject, PlanRow, PlanSection } from "../models/plan-types";
import { NAME_WIDTH, SECTION_PADDING } from "./plan-geometry";
import { PlanRowView } from "./PlanRowView";
import styles from "./DepartmentSection.module.scss";

export interface DepartmentRow {
    row: PlanRow;
    /** Индекс в плоском массиве строк — им адресуются все операции */
    index: number;
}

interface DepartmentSectionProps {
    department: string;
    /** Отдел пользователя, который правит только свой отдел: стоит первым, с меткой «Ваш отдел» */
    own?: boolean;
    rows: DepartmentRow[];
    columns: PlanColumn[];
    projects: Map<string, PlanProject>;
    sections: Map<string, PlanSection>;
    /** Можно ли править план сотрудника — остальные строки только для чтения */
    canEdit: (employerId: string) => boolean;
    /** На странице есть что править — тогда строки только для чтения чуть бледнее */
    canEditAny: boolean;
    /** Ссылка в карточку сотрудника; null — нет прав */
    employeeUrl: (employerId: string) => string | null;
    selection: CellSelection | null;
    dropPreview: CellSelection | null;
    /** Раскладка сетки: офсеты колонок и слоты */
    grid: PlanGrid;
    /** Слот (день или неделя), выделенный кликом по шапке */
    selectedSlot: { start: number; length: number } | null;
    monthBreaks: number[];
    collapsed: boolean;
    onToggle: (department: string) => void;
    onBlockClick: (rowIndex: number, start: number, length: number) => void;
    onDayActivate: (rowIndex: number, column: number, x: number, y: number) => void;
    onDayContext: (rowIndex: number, column: number, x: number, y: number) => void;
}

export const DepartmentSection: React.FC<DepartmentSectionProps> = (props) => {
    const {
        department,
        own,
        rows,
        columns,
        projects,
        sections,
        canEdit,
        canEditAny,
        employeeUrl,
        selection,
        dropPreview,
        grid,
        selectedSlot,
        monthBreaks,
        collapsed,
        onToggle,
        onBlockClick,
        onDayActivate,
        onDayContext,
    } = props;

    const toggle = useCallback(() => onToggle(department), [onToggle, department]);

    // Свёрнутый отдел не рендерит строк, и max-content схлопнул бы блок до ширины
    // названия. Держим ширину сетки явно — серая шапка остаётся во всю таблицу.
    const width = NAME_WIDTH + grid.totalWidth + SECTION_PADDING * 2;

    return (
        <div className={`${styles.block} department-block`} style={{ minWidth: width }}>
            <h1 className="planr-block-header-wide_lowscreen collapser" onClick={toggle}>
                <div className={styles.departmentName}>
                    <Caret collapsed={collapsed} />

                    <div className={styles.department}>{department}</div>

                    <Peoples count={rows.length} />

                    {own && <span className={styles.ownBadge}>Ваш отдел</span>}
                </div>
            </h1>

            <Collapse isOpen={!collapsed} keepChildrenMounted={false}>
                <div className="collapse-wide_lowscreen" style={{ paddingTop: "0px" }}>
                    {rows.map(({ row, index }) => (
                        <PlanRowView
                            key={row.employerId}
                            row={row}
                            rowIndex={index}
                            columns={columns}
                            projects={projects}
                            sections={sections}
                            readOnly={!canEdit(row.employerId)}
                            dimmed={canEditAny && !canEdit(row.employerId)}
                            employeeUrl={employeeUrl(row.employerId)}
                            selection={selection}
                            dropPreview={
                                dropPreview && dropPreview.rowIndex === index ? dropPreview : null
                            }
                            grid={grid}
                            selectedSlot={selectedSlot}
                            monthBreaks={monthBreaks}
                            onBlockClick={onBlockClick}
                            onDayActivate={onDayActivate}
                            onDayContext={onDayContext}
                        />
                    ))}
                </div>
            </Collapse>
        </div>
    );
};
